import React, { ReactNode } from 'react';
import { Box, useMediaQuery, useTheme, Divider, Drawer, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useUIState } from '../../store/hooks';
import { thinScrollbarStyles } from '../../styles/globalStyles';

interface AssistantLayoutProps {
  main: ReactNode;
  sidebar?: ReactNode;
  topBar?: ReactNode;
  notificationCount?: number;
  /** Use content when the route supplies its own bounded scroll region. */
  mainScrollOwner?: 'layout' | 'content';
}

export const AssistantLayout: React.FC<AssistantLayoutProps> = ({
  main,
  sidebar,
  topBar,
  notificationCount,
  mainScrollOwner = 'layout',
}) => {
  const { sidebarOpen, toggleSidebar: onToggleSidebar } = useUIState();
  const theme = useTheme();
  const isSidebarDrawer = useMediaQuery(theme.breakpoints.down('lg'));

  // Tooltip text moved up here for better readability
  const getTooltipText = () => {
    if (sidebarOpen) {
      return 'Hide assistant';
    } else if (notificationCount && notificationCount > 0) {
      return notificationCount === 1
        ? '1 suggestion from assistant'
        : `${notificationCount} suggestions from assistant`;
    } else {
      return 'Show assistant';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        position: 'relative',
      }}
    >
      {topBar && <Box sx={{ flexShrink: 0, minWidth: 0 }}>{topBar}</Box>}
      <Box
        sx={{
          display: 'flex',
          flex: '1 1 0',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          px: 0,
        }}
      >
        {/* Main Content */}
        <Box
          sx={{
            ...thinScrollbarStyles,
            height: '100%',
            flex: '1 1 0',
            minHeight: 0,
            minWidth: 0,
            overflow: mainScrollOwner === 'layout' ? 'auto' : 'hidden',
            display: 'flex',
            justifyContent: 'center',
            px: 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: mainScrollOwner === 'content' ? '100%' : undefined,
              minWidth: 0,
              mx: 'auto',
              p: 0,
            }}
          >
            {main}
          </Box>
        </Box>

        {/* Desktop Right Sidebar */}
        {!isSidebarDrawer && sidebar && (
          <Box
            sx={{
              '--jot-sidebar-width': '25vw',
              width: sidebarOpen ? 'var(--jot-sidebar-width)' : '0px',
              flexShrink: 0,
              flexGrow: 0,
              transition: 'width 0.3s ease-in-out',
              overflow: 'visible',
              position: 'relative',
              height: '100%',
              backgroundColor: 'grey.100',
            }}
          >
            <Tooltip title={getTooltipText()} placement="left" arrow>
              <IconButton
                onClick={onToggleSidebar}
                sx={theme => ({
                  position: 'absolute',
                  left: '-27px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  width: '28px',
                  height: '100px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '0 4px 4px 0',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  ...(!sidebarOpen && {
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[3],
                  }),
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem',
                  },
                })}
              >
                {sidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>

            {typeof notificationCount !== 'undefined' && notificationCount > 0 && (
              <Box
                sx={theme => ({
                  position: 'absolute',
                  top: 'calc(50% - 50px - 5px)',
                  left: 'calc(-27px - 5px)',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.warning.main,
                  zIndex: 2,
                  opacity: sidebarOpen ? 0 : 1,
                })}
              />
            )}

            <Box
              sx={{
                width: 'var(--jot-sidebar-width)',
                transform: sidebarOpen ? 'translateX(0)' : 'translateX(var(--jot-sidebar-width))',
                transition: 'transform 0.3s ease-in-out',
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Sidebar content */}
              <Box>{sidebar}</Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Mobile Drawer */}
      {/* {isSidebarDrawer && (
        <Drawer
          anchor="bottom"
          open={sidebarOpen}
          // onClose={() => dispatch(toggleSidebar())}
          variant="persistent"
          sx={{
            "& .MuiDrawer-paper": {
              height: `50vh`,
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              py: 2,
              px: 2,
              transform: sidebarOpen ? "translateY(0)" : `translateY(100%px)`,
              transition: "transform 0.3s ease-in-out",
            },
          }}
        >
          <Box sx={{ overflowY: "auto", height: "100%" }}>{sidebar}</Box>
        </Drawer>
      )} */}
    </Box>
  );
};
